import { Button, Flex } from '@/components';
import { ProjectResourceGuard } from '@/features/authentication/_components/ProjectResourceGuard';
import { useMe } from '@/features/authentication/_hooks/me';
import { useProjectsPageSearchQueryStates } from '@/features/project/_hooks/search-projects/search-query-states';
import {
  convertInputValueToRequestParams,
  convertRequestParams,
} from '@/features/project/_utils/convert-request-params';
import { FilterResult } from '@/features/project/projects/_components/FilterResult';
import { ProjectsSearch } from '@/features/search/_components/projects/ProjectsSearch';
import type { ProjectsSearchSubmitType } from '@/features/search/_utils/projects-search';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import { ExportOutlined } from '@ant-design/icons';
import { type FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateGroupTargets } from '../../_hooks/group';
import { useGroupTargetExtend } from '../_hooks/group';

type Props = {
  groupFiscalYear: string;
  groupInProjectIds: string[];
};

export const ProjectsTable: FC<Props> = ({
  groupFiscalYear,
  groupInProjectIds,
}) => {
  const { t } = useTranslation();
  const { id } = useId();
  const { openMessage } = useMessage();
  const me = useMe();
  const { mutateAsync: updateGroupTargetsAsync } = useUpdateGroupTargets(id);
  const { mutateAsync: updateGroupTargetExtend } = useGroupTargetExtend(id);

  const isViewAllPrivilege =
    me.data?.role.resourceGroupPermissions.project === 'view-all-privilege';

  const { setSearchQueryStates, searchQueryStates } =
    useProjectsPageSearchQueryStates();

  const handleSearchSubmit = useCallback(
    async ({ values, newCascadeId }: ProjectsSearchSubmitType) => {
      const requestParams = convertInputValueToRequestParams(
        values,
        newCascadeId,
      );
      setSearchQueryStates((prev) => ({ ...prev, ...requestParams }));
    },
    [setSearchQueryStates],
  );

  return (
    <Flex vertical gap={16}>
      <ProjectsSearch
        initialFiscalYear={groupFiscalYear}
        onSubmit={handleSearchSubmit}
        type="group"
      />
      <FilterResult
        isFromGroupPage
        groupFiscalYear={groupFiscalYear}
        addAllProjectButton={
          <ProjectResourceGuard>
            <Button
              onClick={async () => {
                const requestParams = convertRequestParams({
                  searchQueryStates,
                  isDisplayPolicyLevel: false,
                  sorting: '',
                  groupFiscalYear,
                });
                try {
                  await updateGroupTargetExtend(requestParams);
                  await openMessage('success', t('message-update-completed'));
                } catch {
                  await openMessage('error', t('message-update-failed'));
                }
              }}
            >
              {t('group-project-bulk-operation-add-all-projects')}
            </Button>
          </ProjectResourceGuard>
        }
        projectTableActionsColumn={{
          title: '',
          key: 'actions',
          fixed: 'right',
          width: 100,
          render: (_, record) => {
            const added = groupInProjectIds.includes(record.id);
            return (
              <Flex justify="flex-end" gap={8}>
                {(record.isEditable || isViewAllPrivilege) && (
                  <ProjectResourceGuard>
                    <Button
                      style={{ width: 75 }}
                      onClick={async () => {
                        await updateGroupTargetsAsync([
                          ...groupInProjectIds,
                          record.id,
                        ]);
                      }}
                      disabled={added}
                    >
                      {added
                        ? t('group-project-operation-added-project')
                        : t('group-project-operation-add-project')}
                    </Button>
                  </ProjectResourceGuard>
                )}
                <Button
                  type="link"
                  icon={<ExportOutlined aria-label="" />}
                  onClick={() => {
                    window.open(`/rs/project/${record.id}`, '_blank');
                  }}
                >
                  {t('details')}
                </Button>
              </Flex>
            );
          },
        }}
      />
    </Flex>
  );
};
